import {
    useEffect,
    useState
} from "react";

import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";

import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

import {
    EndpointReplyCustomJavascriptFileFormat,
    IContextBase,
    IEndpointSplitByField,
    IEndpointTestExample
} from "../lib/backend/extractions.types.generated";
import { BackendObj } from "../lib/backend";
import { generateInputTextDefinition, generateTemplateTypeDefinition } from "../lib/utils";

import { FullScreen } from "./FullScreen";
import { HandlebarsDoc } from "./HandlebarsDoc";
import { Textbox } from "./Textbox";
import { Dropdown } from "./Dropdown";
import { Button, IconButton } from "./Button";

export interface IHandlebarDetails {
    handlebar_template: string;
    handlebar_file_extension: string;
    handlebar_file_mimetype: string;
}

type AttachmentHandlebarDetailsProps = {
    template_uuid: string;
    split_by_field?: IEndpointSplitByField[];
    handlebar_template: string;
    handlebar_file_extension: string;
    handlebar_file_mimetype: string;
    onClose: (handlebar_details: IHandlebarDetails) => void;
}

export function AttachmentHandlebarDetails({
    template_uuid,
    split_by_field,
    handlebar_template: init_handlebar_template,
    handlebar_file_extension: init_handlebar_file_extension,
    handlebar_file_mimetype: init_handlebar_file_mimetype,
    onClose
}: AttachmentHandlebarDetailsProps) {

    const [handlebar_template, setHandlebarTemplate] = useState(init_handlebar_template);
    const [handlebar_file_extension, setHandlebarFileExtension] = useState(init_handlebar_file_extension);
    const [handlebar_file_mimetype, setHandlebarFileMimetype] = useState(init_handlebar_file_mimetype);
    const [is_handlebar_help_open, setIsHandlebarHelpOpen] = useState<boolean>(false);
    const [endpoint_test_json_input, setEndpointTestJsonInput] = useState<string>("");
    const [endpoint_test_result, setEndpointTestResult] = useState<string>("");
    const [endpoint_test_examples, setEndpointTestExamples] = useState<IEndpointTestExample[] | undefined>(undefined);

    useEffect(() => {
        BackendObj.extractions.getEndpointTestExamples({ template_uuid, split_by_field, output_type: "scrapes_rendered" })
            .then(({ examples }) => { setEndpointTestExamples(examples); })
            .catch((err) => { console.error(err); });
    }, [template_uuid, split_by_field]);

    const handleTest = async () => {
        try {
            const res = await BackendObj.extractions.getHandlebarsResult({ template: handlebar_template, data: endpoint_test_json_input })
            if (res.error !== undefined) {
                setEndpointTestResult(`${res.error}`);
            } else {
                setEndpointTestResult(res.rendered_template);
            }
        } catch (err) {
            setEndpointTestResult(`${err}`);
        }
    }

    const handleClose = () => {
        onClose({ handlebar_template, handlebar_file_extension, handlebar_file_mimetype });
    }

    return <FullScreen show={true} show_close_button={false} full_height={false} opacity={false} onClose={handleClose}>
        <div className="p-4 flex flex-col gap-y-4">
            <div className="flex flex-row items-center gap-x-2">
                <h1 className="text-base font-semibold leading-6 text-gray-900">Handlebar Template</h1>
                <div className="flex-grow" />
                <IconButton icon={XMarkIcon} onClick={handleClose} size="large" />
            </div>

            <div className="flex flex-col gap-y-2">
                <label htmlFor="endpoint_reply_file_extension" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    <p>Reply file extension</p>
                </label>
                <Textbox
                    id="endpoint_reply_file_extension"
                    value={handlebar_file_extension}
                    onChange={(value) => { setHandlebarFileExtension(value) }}
                />
            </div>

            <div className="flex flex-col gap-y-2">
                <label htmlFor="endpoint_reply_file_mimetype" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    <p>Reply file mimetype</p>
                </label>
                <Textbox
                    id="endpoint_reply_file_mimetype"
                    value={handlebar_file_mimetype}
                    onChange={(value) => { setHandlebarFileMimetype(value) }}
                />
            </div>

            <div className="flex flex-col gap-y-2">
                <div className="flex flex-row items-center">
                    <span className="text-sm font-medium leading-6 text-gray-900">Use handlebar notation to define custom format</span>
                    <span><QuestionMarkCircleIcon className="text-gray-400 w-4 h-4 ml-2 cursor-pointer" onClick={() => setIsHandlebarHelpOpen(true)} /></span>
                </div>
                <FullScreen show={is_handlebar_help_open} show_close_button={true} onClose={() => setIsHandlebarHelpOpen(false)}>
                    <HandlebarsDoc />
                </FullScreen>
                <div className="w-full shadow border">
                    <CodeMirror
                        value={handlebar_template}
                        height="500px"
                        theme="light"
                        onChange={(value) => { setHandlebarTemplate(value) }} />
                </div>
            </div>
            <div className="flex flex-col gap-y-2">
                <div className="my-4 flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <div className="w-1/2">
                            <span className="text-sm font-medium leading-6 text-gray-900">JSON Input</span>
                            <div className="w-full shadow border mt-2">
                                <CodeMirror
                                    value={endpoint_test_json_input}
                                    height="200px"
                                    theme="light"
                                    onChange={(value) => {
                                        setEndpointTestJsonInput(value);
                                    }} />
                            </div>
                        </div>
                        <div className="w-1/2">
                            <span className="text-sm font-medium leading-6 text-gray-900">Handlebar Result</span>
                            <div className="w-full shadow border mt-2">
                                <CodeMirror
                                    value={endpoint_test_result}
                                    height="200px"
                                    theme="light"
                                    readOnly={true} />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-between items-center">
                        <div className="grow">
                            <Dropdown
                                values={[
                                    "Select an example",
                                    ...endpoint_test_examples?.map(example => `${example.name}${example.comments[0] && example.comments[0].comment ? " - " : ""}${example.comments[0]?.comment ?? ""}`) || []
                                ]}
                                ids={[
                                    "",
                                    ...endpoint_test_examples?.map((_, index) => index.toString()) || []
                                ]}
                                selected=""
                                onChange={(selectedIndex) => {
                                    const index = parseInt(selectedIndex, 10);
                                    if (!isNaN(index) && endpoint_test_examples && index >= 0 && index < endpoint_test_examples.length) {
                                        setEndpointTestJsonInput(endpoint_test_examples[index].json);
                                    }
                                }}
                            />
                        </div>
                        <Button text="Test" onClick={handleTest} />
                    </div>
                </div>
            </div>
            <div className="flex flex-row justify-end">
                <Button text="Close" highlight={true} onClick={handleClose} />
            </div>
        </div>
    </FullScreen>;
}

export interface ICustomJavascriptDetails {
    custom_javascript_file_format: EndpointReplyCustomJavascriptFileFormat;
    custom_javascript_formula: string;
}

type AttachmentJavascriptDetailsProps = {
    template_uuid: string;
    contexts: IContextBase[];
    split_by_field?: IEndpointSplitByField[];
    custom_javascript_file_format: EndpointReplyCustomJavascriptFileFormat;
    custom_javascript_formula: string;
    onClose: (custom_javascript_details: ICustomJavascriptDetails) => void;
}

export function AttachmentJavascriptDetails({
    template_uuid,
    contexts,
    split_by_field,
    custom_javascript_file_format: init_custom_javascript_file_format,
    custom_javascript_formula: init_custom_javascript_formula,
    onClose
}: AttachmentJavascriptDetailsProps) {
    const [custom_javascript_file_format, setCustomJavascriptFileFormat] = useState(init_custom_javascript_file_format);
    const [custom_javascript_formula, setCustomJavascriptFormula] = useState(init_custom_javascript_formula);
    const [endpoint_test_json_input, setEndpointTestJsonInput] = useState<string>("");
    const [endpoint_test_result, setEndpointTestResult] = useState<string>("");
    const [endpoint_test_examples, setEndpointTestExamples] = useState<IEndpointTestExample[] | undefined>(undefined);

    useEffect(() => {
        BackendObj.extractions.getEndpointTestExamples({ template_uuid, split_by_field, output_type: "scrapes_raw" })
            .then(({ examples }) => { setEndpointTestExamples(examples); })
            .catch((err) => { console.error(err); });
    }, [template_uuid, split_by_field]);

    const handleTest = async () => {
        BackendObj.extractions.getCustomJavascriptResult(
            { formula: custom_javascript_formula, data: endpoint_test_json_input, template_uuid })
            .then((res) => {
                if (res.error !== undefined) {
                    setEndpointTestResult(`${res.error}`);
                } else {
                    setEndpointTestResult(res.result);
                }
            }).catch((err) => {
                setEndpointTestResult(`${err}`);
            });
    };

    const handleClose = async () => {
        onClose({ custom_javascript_file_format, custom_javascript_formula });
    }

    return <FullScreen show={true} show_close_button={false} full_height={false} opacity={false} onClose={handleClose}>
        <div className="flex flex-row items-center gap-x-2">
            <h1 className="text-base font-semibold leading-6 text-gray-900">Javascript Template</h1>
            <div className="flex-grow" />
            <IconButton icon={XMarkIcon} onClick={handleClose} size="large" />
        </div>

        <label htmlFor="endpoint_custom_javascript_file_format" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 col-span-2">
            <p>Output format</p>
            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                All non-JSON outputs require an array of arrays returned from custom javascript.
            </p>
        </label>
        <div className="mt-2 sm:mt-0 sm:col-span-1">
            <Dropdown
                values={["JSON", "Excel", "CSV", "TSV"]}
                ids={["json", "xlsx", "csv", "tsv"]}
                selected={custom_javascript_file_format}
                onChange={(format) => { setCustomJavascriptFileFormat(format as EndpointReplyCustomJavascriptFileFormat); }}
            />
        </div>
        <div className="py-4">
            <div className="my-2 sm:mt-0">
                <div className="flex flex-row items-center pb-2">
                    <span className="text-sm font-medium leading-6 text-gray-900">Use custom javascript to compute the final output</span>
                </div>
                <div className="w-full shadow border mb-3">
                    <CodeMirror
                        value={custom_javascript_formula}
                        minHeight="200px"
                        maxHeight="500px"
                        theme="light"
                        extensions={[javascript()]}
                        onChange={(value) => { setCustomJavascriptFormula(value) }} />
                </div>
                <span className="text-sm font-medium leading-6 text-gray-900">Available variables</span>
                <div className="w-full shadow border mt-2">
                    <CodeMirror
                        readOnly={true}
                        maxHeight="500px"
                        value={
                            generateTemplateTypeDefinition(contexts) +
                            generateInputTextDefinition()
                        }
                        extensions={[javascript({ typescript: true })]}
                        theme="light" />
                </div>
                <div className="my-4 flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <div className="w-1/2">
                            <span className="text-sm font-medium leading-6 text-gray-900">JSON Input</span>
                            <div className="w-full shadow border mt-2">
                                <CodeMirror
                                    value={endpoint_test_json_input}
                                    height="200px"
                                    theme="light"
                                    extensions={[json()]}
                                    onChange={(value) => {
                                        setEndpointTestJsonInput(value);
                                    }} />
                            </div>
                        </div>
                        <div className="w-1/2">
                            <span className="text-sm font-medium leading-6 text-gray-900">Result</span>
                            <div className="w-full shadow border mt-2">
                                <CodeMirror
                                    value={endpoint_test_result}
                                    height="200px"
                                    theme="light"
                                    extensions={[json()]}
                                    readOnly={true} />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-between items-center">
                        <div className="w-64">
                            <Dropdown
                                values={[
                                    "Select an example",
                                    ...endpoint_test_examples?.map(example => `${example.name}${example.comments[0] && example.comments[0].comment ? " - " : ""}${example.comments[0]?.comment ?? ""}`) || []
                                ]}
                                ids={[
                                    "",
                                    ...endpoint_test_examples?.map((_, index) => index.toString()) || []
                                ]}
                                selected=""
                                onChange={(selectedIndex) => {
                                    const index = parseInt(selectedIndex, 10);
                                    if (!isNaN(index) && endpoint_test_examples && index >= 0 && index < endpoint_test_examples.length) {
                                        setEndpointTestJsonInput(endpoint_test_examples[index].json);
                                    }
                                }}
                            />
                        </div>
                        <Button text="Test" onClick={handleTest} />
                    </div>
                </div>
            </div>
        </div>
        <div className="flex flex-row justify-end">
            <Button text="Close" highlight={true} onClick={handleClose} />
        </div>
    </FullScreen>;
}